import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../firebase';

const Oracle = ({ user, userProfile }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [conversations, setConversations] = useState([]);

  useEffect(() => {
    fetchConversations();
    // Add initial greeting
    setMessages([{
      id: 1,
      role: 'assistant',
      content: 'I am your Oracle - an AI agent acting as a medium through whom advice or prophecy may be sought. Upload your documents, share your thoughts, and I shall provide wisdom and insights on any topic.',
      timestamp: new Date()
    }]);
  }, []);

  const fetchConversations = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/oracle/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setConversations(response.data.conversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/oracle/chat`,
        { 
          message: inputMessage,
          files: files.map(f => ({ id: f.id, name: f.name, type: f.type }))
        },
        { headers: { 'Authorization': `Bearer ${token}` }}
      );

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'Sorry, there was an error processing your request.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = await auth.currentUser.getIdToken();
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/oracle/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setFiles(prev => [...prev, response.data.file]);
      
      // Add a message about the uploaded file
      const uploadMessage = {
        id: Date.now(),
        role: 'assistant',
        content: `Document received: ${file.name}. I can now reference this knowledge in our conversations.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, uploadMessage]);
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const styles = {
    container: {
      height: '80vh',
      display: 'flex',
      flexDirection: 'column',
      background: 'white',
      borderRadius: '8px',
      overflow: 'hidden'
    },
    header: {
      padding: '20px',
      background: '#f8f9fa',
      borderBottom: '1px solid #dee2e6'
    },
    chatContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      background: '#f5f5f5'
    },
    message: {
      marginBottom: '20px',
      maxWidth: '80%'
    },
    userMessage: {
      marginLeft: 'auto',
      background: '#007AFF',
      color: 'white',
      padding: '15px',
      borderRadius: '15px 15px 0 15px'
    },
    assistantMessage: {
      background: 'white',
      padding: '15px',
      borderRadius: '15px 15px 15px 0',
      border: '1px solid #ddd'
    },
    messageHeader: {
      fontWeight: 'bold',
      marginBottom: '8px',
      fontSize: '14px'
    },
    inputContainer: {
      padding: '20px',
      background: 'white',
      borderTop: '1px solid #dee2e6',
      display: 'flex',
      gap: '10px',
      alignItems: 'flex-end'
    },
    textarea: {
      flex: 1,
      padding: '15px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      resize: 'vertical',
      minHeight: '50px',
      fontFamily: 'inherit'
    },
    button: {
      padding: '15px 20px',
      background: '#007AFF',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer'
    },
    fileUpload: {
      padding: '10px',
      background: '#28a745',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer'
    },
    fileList: {
      padding: '10px 20px',
      background: '#e9ecef',
      borderBottom: '1px solid #dee2e6'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3>Oracle</h3>
        <p>Your personal AI agent - a medium through whom advice or prophecy is sought. Upload any documents and seek wisdom from your digital oracle.</p>
      </div>

      {files.length > 0 && (
        <div style={styles.fileList}>
          <strong>Uploaded Documents:</strong>
          {files.map(file => (
            <span key={file.id} style={{ marginLeft: '10px', padding: '4px 8px', background: '#007AFF', color: 'white', borderRadius: '4px', fontSize: '12px' }}>
              {file.name}
            </span>
          ))}
        </div>
      )}

      <div style={styles.chatContainer}>
        {messages.map(message => (
          <div
            key={message.id}
            style={{
              ...styles.message,
              ...(message.role === 'user' ? styles.userMessage : styles.assistantMessage)
            }}
          >
            <div style={styles.messageHeader}>
              {message.role === 'user' ? 'Human User:' : 'Oracle (AI Agent):'}
            </div>
            <div style={{ whiteSpace: 'pre-wrap' }}>
              {message.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={styles.assistantMessage}>
            <div style={styles.messageHeader}>Oracle (AI Agent):</div>
            <div>Consulting the digital realm for insights...</div>
          </div>
        )}
      </div>

      <div style={styles.inputContainer}>
        <input
          type="file"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          id="file-upload"
          accept=".pdf,.txt,.doc,.docx,.jpg,.jpeg,.png"
        />
        <label htmlFor="file-upload" style={styles.fileUpload}>
          Upload Document
        </label>
        <textarea
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Seek wisdom from your Oracle about any topic..."
          style={styles.textarea}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <button onClick={sendMessage} disabled={loading} style={styles.button}>
          Send
        </button>
      </div>
    </div>
  );
};

export default Oracle;